import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { useAuth } from "react-oidc-context";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button, ChevronDown, FarmacenterCross } from "@moodstudios/farmacenter-design";
import {
  Barcode, Buildings, Calculator, CashRegister, ChartBar, ClipboardText, Desktop, FilePlus,
  FileText, Files, Gear, Graph, HandCoins, House, IdentificationBadge, Money, NotePencil,
  Package, Receipt, ShoppingCart, Stack, Table, Tag, TreeStructure, Users, UsersThree,
  type Icon,
} from "@phosphor-icons/react";
import { useTheme } from "./useTheme";
import { OrgSelector } from "./OrgSelector";
import { Avatar } from "./ui/Avatar";
import { useAsistenteApi } from "./asistente/asistenteApi";
import { EtiquetaCategoria } from "./ui/primitivas";
import { BarraTeclas, useDisparadorTeclas, type MapaTeclas } from "./ui/teclas";

interface Seccion {
  ruta: string;
  nombre: string;
  /** Código corto para saltar a la pantalla sin ratón (memoria muscular Dominium). */
  codigo: string;
  /** Icono Phosphor de la entrada del menú. */
  icono: Icon;
}

interface Grupo {
  /** Título del grupo, o null para las entradas sueltas de arriba. */
  titulo: string | null;
  items: Seccion[];
}

/**
 * Menú del backoffice, ordenado y nombrado como el de Dominium para conservar la
 * memoria muscular: un trabajador de Dominium encuentra cada cosa donde la espera.
 * Orden de Dominium (izq→der): Ventas · Compras · Inventario · Cartera · Cuentas por
 * pagar · Contabilidad · Reportes · Terceros · Fidelización · Utilidades · Parámetros.
 * "Panel" es una elevación nuestra (Dominium no tiene tablero). Faltan pantallas para
 * Cuentas por pagar y Fidelización, así que esos grupos no se listan aún (no se pone
 * un menú que no lleva a ningún sitio — estado honesto).
 */
const GRUPOS: Grupo[] = [
  { titulo: null, items: [{ ruta: "/", nombre: "Panel", codigo: "INI", icono: House }] },
  {
    titulo: "Ventas",
    items: [
      { ruta: "/ventas", nombre: "Ventas", codigo: "VEN", icono: ShoppingCart },
      { ruta: "/documentos", nombre: "Documentos electrónicos", codigo: "DOC", icono: FileText },
    ],
  },
  {
    titulo: "Compras",
    items: [
      { ruta: "/compras/factura-nueva", nombre: "Registrar factura", codigo: "FC", icono: FilePlus },
      { ruta: "/compras/facturas", nombre: "Facturas de compra", codigo: "FCL", icono: Files },
      { ruta: "/compras/ordenes", nombre: "Órdenes", codigo: "ORD", icono: ClipboardText },
      { ruta: "/compras/recepciones", nombre: "Recepciones", codigo: "REC", icono: Package },
      { ruta: "/compras/borradores", nombre: "Archivo Coopidrogas", codigo: "COO", icono: Files },
      { ruta: "/compras/actas", nombre: "Actas de recepción", codigo: "ACT", icono: ClipboardText },
    ],
  },
  {
    titulo: "Inventario",
    items: [
      { ruta: "/existencias", nombre: "Existencias", codigo: "EXI", icono: Stack },
      { ruta: "/inventario/lotes", nombre: "Lotes", codigo: "LOT", icono: Barcode },
    ],
  },
  { titulo: "Cartera", items: [{ ruta: "/cartera/pagos", nombre: "Recibos de cliente", codigo: "CAR", icono: HandCoins }] },
  {
    titulo: "Caja",
    items: [
      { ruta: "/caja/sesiones", nombre: "Sesiones", codigo: "CAJ", icono: CashRegister },
      { ruta: "/caja/recibos", nombre: "Recibos", codigo: "RCB", icono: Receipt },
      { ruta: "/caja/desembolsos", nombre: "Desembolsos", codigo: "EGR", icono: Money },
    ],
  },
  {
    titulo: "Contabilidad",
    items: [
      { ruta: "/contabilidad/plan-cuentas", nombre: "Plan de cuentas", codigo: "PUC", icono: TreeStructure },
      { ruta: "/contabilidad/reglas", nombre: "Reglas de causación", codigo: "RCA", icono: Gear },
      { ruta: "/contabilidad/reglas-nodos", nombre: "Mapa de causación", codigo: "MCA", icono: Graph },
      { ruta: "/contabilidad/comprobante", nombre: "Comprobante manual", codigo: "CMP", icono: NotePencil },
      { ruta: "/contabilidad/asientos", nombre: "Consulta de asientos", codigo: "ASI", icono: Table },
    ],
  },
  { titulo: "Reportes", items: [{ ruta: "/contabilidad/reportes", nombre: "Contables", codigo: "RPT", icono: ChartBar }] },
  { titulo: "Terceros", items: [{ ruta: "/configuracion/terceros", nombre: "Clientes y proveedores", codigo: "TER", icono: UsersThree }] },
  { titulo: "Utilidades", items: [{ ruta: "/pos/estado", nombre: "Estado del POS", codigo: "POS", icono: Desktop }] },
  {
    titulo: "Parámetros",
    items: [
      { ruta: "/configuracion", nombre: "Organización", codigo: "ORG", icono: Buildings },
      { ruta: "/usuarios", nombre: "Usuarios y permisos", codigo: "USR", icono: Users },
      { ruta: "/roles", nombre: "Roles", codigo: "ROL", icono: IdentificationBadge },
      { ruta: "/configuracion/catalogo", nombre: "Catálogo de productos", codigo: "CAT", icono: Tag },
      { ruta: "/configuracion/contabilidad", nombre: "Contabilidad", codigo: "PCT", icono: Calculator },
      { ruta: "/configuracion/compras", nombre: "Proveedor de compras", codigo: "PCO", icono: Gear },
    ],
  },
];

/** Todas las pantallas en plano, para el buscador de "Ir a" (nombre o código). */
const PANTALLAS = GRUPOS.flatMap((g) => g.items);

/** Mapa tecla → ruta, derivado de GRUPOS (una sola fuente de verdad). */
export function AppShell({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const { modo, alternar } = useTheme();
  const { pathname } = useLocation();
  const [ayuda, setAyuda] = useState(false);

  const perfil = auth.user?.profile;
  const nombre = perfil?.name ?? perfil?.preferred_username ?? "Usuario";

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const saltoRef = useRef<HTMLInputElement>(null);
  const disparar = useDisparadorTeclas();
  const ayudaRef = useRef(ayuda);
  ayudaRef.current = ayuda;
  const mainRef = useRef<HTMLElement>(null);

  // Al cambiar de pantalla, el contenido vuelve arriba. El scroll vive en <main>, no en la
  // ventana, así que hay que resetear su scrollTop; si no, la pantalla nueva se abría a la
  // altura de scroll de la anterior (a veces al fondo).
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
  }, [pathname]);

  // Latido de presencia app-wide: mientras el backoffice esté abierto, este usuario cuenta como
  // "conectado al ERP" (el Panel lo lee). Falla en silencio si el endpoint aún no está desplegado.
  const asistApi = useAsistenteApi();
  useEffect(() => {
    let vivo = true;
    const latir = () => {
      if (vivo) void asistApi.presencia().catch(() => {});
    };
    latir();
    const id = window.setInterval(latir, 30_000);
    return () => {
      vivo = false;
      window.clearInterval(id);
    };
  }, [asistApi]);

  // Teclas activas en CUALQUIER pantalla; la pantalla actual las puede sobrescribir.
  // F5 recarga los datos visibles, F8 imprime, Esc vuelve atrás.
  const globales: MapaTeclas = {
    F5: { etiqueta: "Actualizar", accion: () => void queryClient.invalidateQueries() },
    F8: { etiqueta: "Imprimir", accion: () => window.print() },
    Escape: { etiqueta: "Volver", accion: () => navigate(-1) },
  };
  const globalesRef = useRef(globales);
  globalesRef.current = globales;

  // F1 abre/cierra la ayuda. Las demás teclas de función disparan lo que la pantalla
  // actual haya registrado (useTeclas), y se muestran en la barra inferior estilo
  // Dominium. Esc cierra la ayuda si está abierta; si no, dispara "salir".
  useEffect(() => {
    const FUNCION = ["F2", "F3", "F4", "F5", "F6", "F8", "F10"];
    const onKey = (e: KeyboardEvent) => {
      // Ctrl+K abre el "Ir a" desde cualquier sitio (estándar de paleta de comandos).
      if (e.ctrlKey && !e.altKey && !e.metaKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        saltoRef.current?.focus();
        return;
      }
      if (e.ctrlKey || e.altKey || e.metaKey || e.shiftKey) return;
      if (e.key === "/") {
        const a = document.activeElement;
        const enCampo =
          a instanceof HTMLElement && ["INPUT", "TEXTAREA", "SELECT"].includes(a.tagName);
        if (!enCampo) {
          e.preventDefault();
          saltoRef.current?.focus();
        }
        return;
      }
      if (e.key === "F1") {
        e.preventDefault();
        setAyuda((v) => !v);
      } else if (e.key === "Escape") {
        if (ayudaRef.current) setAyuda(false);
        else if (disparar("Escape")) e.preventDefault();
        else globalesRef.current.Escape?.accion();
      } else if (FUNCION.includes(e.key)) {
        if (disparar(e.key)) {
          e.preventDefault();
        } else {
          const g = globalesRef.current[e.key as keyof MapaTeclas];
          if (g) {
            e.preventDefault();
            g.accion();
          }
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="grid h-screen grid-cols-[240px_1fr] overflow-hidden bg-surface-raised text-text max-md:grid-cols-1">
      {/* -------------------------------------------------- barra lateral */}
      <aside className="flex flex-col gap-2 overflow-y-auto border-r border-border bg-surface p-4 max-md:hidden">
        <div className="flex items-center gap-2.5 px-2 pb-4 pt-2">
          <FarmacenterCross size={30} title="Farmacenter" />
          <div className="min-w-0">
            <p className="font-data text-2xs font-semibold uppercase tracking-[0.16em] text-action">
              Farmacenter
            </p>
            <p className="font-brand text-lg font-bold leading-none tracking-tight">Administración</p>
          </div>
        </div>
        <nav className="flex flex-col gap-5">
          {GRUPOS.map((grupo) => (
            <div key={grupo.titulo ?? "_"} className="flex flex-col gap-0.5">
              {grupo.titulo ? (
                <div className="mb-1 px-3">
                  <EtiquetaCategoria>{grupo.titulo}</EtiquetaCategoria>
                </div>
              ) : null}
              {grupo.items.map((s) => {
                const activa = pathname === s.ruta;
                const Icono = s.icono;
                return (
                  <NavLink
                    key={s.ruta}
                    to={s.ruta}
                    className={`flex items-center justify-between gap-2 rounded-control px-3 py-2 text-sm font-medium transition-colors ${
                      activa
                        ? "bg-action text-on-action"
                        : "text-text hover:bg-surface-raised"
                    }`}
                  >
                    <span className="flex min-w-0 items-center gap-2.5">
                      <Icono size={17} weight={activa ? "fill" : "regular"} className="shrink-0" />
                      <span className="truncate">{s.nombre}</span>
                    </span>
                    <kbd
                      className="shrink-0 rounded px-1 font-data text-2xs"
                      style={{
                        color: activa ? "var(--fc-color-on-action)" : "var(--fc-color-text-muted)",
                        background: activa
                          ? "color-mix(in srgb, var(--fc-color-on-action) 22%, transparent)"
                          : "var(--fc-color-surface-raised)",
                      }}
                    >
                      {s.codigo}
                    </kbd>
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>

      {/* -------------------------------------------------- contenido */}
      <div className="flex min-h-0 min-w-0 flex-col">
        <header className="flex items-center gap-3 border-b border-border bg-surface px-6 py-3">
          <OrgSelector />
          <SaltoCodigo inputRef={saltoRef} navegar={(r) => navigate(r)} />
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="ghost"
              aria-label={modo === "dark" ? "Tema claro" : "Tema oscuro"}
              onClick={alternar}
            >
              {modo === "dark" ? "☀" : "☾"}
            </Button>
            <MenuUsuario nombre={nombre} onSalir={() => void auth.signoutRedirect()} />
          </div>
        </header>

        <main ref={mainRef} className="min-h-0 min-w-0 flex-1 overflow-auto p-6">{children}</main>

        <BarraTeclas
          onAyuda={() => setAyuda(true)}
          globales={globales}
          contexto={
            <>
              <span style={{ color: "var(--fc-color-text)" }}>{nombre}</span>
              <span className="opacity-50">·</span>
              <span>Farmacenter ERP v{__APP_VERSION__}</span>
            </>
          }
        />
      </div>

      {ayuda ? <AyudaAtajos onCerrar={() => setAyuda(false)} /> : null}
    </div>
  );
}

/**
 * Salto por código de pantalla (memoria muscular Dominium): escribe el código y Enter
 * para ir sin ratón. Se enfoca pulsando "/" desde cualquier sitio. Cada pantalla lleva
 * su código a la vista en el menú.
 */
function SaltoCodigo({
  inputRef,
  navegar,
}: {
  inputRef: RefObject<HTMLInputElement | null>;
  navegar: (ruta: string) => void;
}) {
  const [texto, setTexto] = useState("");
  const [abierto, setAbierto] = useState(false);
  const [activo, setActivo] = useState(0);
  const contenedor = useRef<HTMLDivElement>(null);

  const q = texto.trim().toLowerCase();
  const matches = (q
    ? PANTALLAS.filter(
        (s) => s.codigo.toLowerCase().startsWith(q) || s.nombre.toLowerCase().includes(q),
      )
    : PANTALLAS
  ).slice(0, 8);

  useEffect(() => setActivo(0), [q]);
  useEffect(() => {
    const fuera = (e: MouseEvent) => {
      if (contenedor.current && !contenedor.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, []);

  const ir = (ruta: string) => {
    navegar(ruta);
    setTexto("");
    setAbierto(false);
    inputRef.current?.blur();
  };

  return (
    <div ref={contenedor} className="relative flex flex-col gap-1">
      <span className="font-data text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">
        Ir a pantalla
      </span>
      <input
        ref={inputRef}
        value={texto}
        onChange={(e) => {
          setTexto(e.target.value);
          setAbierto(true);
        }}
        onFocus={() => setAbierto(true)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActivo((i) => Math.min(i + 1, matches.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActivo((i) => Math.max(i - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            const m = matches[activo];
            if (m) ir(m.ruta);
          } else if (e.key === "Escape") {
            setTexto("");
            setAbierto(false);
            e.currentTarget.blur();
          }
        }}
        placeholder="Buscar pantalla…  (Ctrl+K)"
        aria-label="Ir a pantalla por nombre o código"
        title="Escribe el nombre o el código de una pantalla · Ctrl+K o / para saltar aquí"
        className="fc-input w-52 rounded-control border px-2.5 py-1.5 text-sm"
        style={{
          borderColor: "var(--fc-color-border-control)",
          background: "var(--fc-color-surface-raised)",
          color: "var(--fc-color-text)",
        }}
      />
      {abierto && matches.length > 0 ? (
        <div
          role="listbox"
          className="absolute top-full z-30 mt-1 w-64 overflow-hidden rounded-md border shadow-lg"
          style={{ background: "var(--fc-color-surface-raised)", borderColor: "var(--fc-color-border)" }}
        >
          {matches.map((s, i) => (
            <button
              key={s.ruta}
              type="button"
              role="option"
              aria-selected={i === activo}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActivo(i)}
              onClick={() => ir(s.ruta)}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm"
              style={{
                background: i === activo ? "var(--fc-color-action)" : "transparent",
                color: i === activo ? "var(--fc-color-on-action)" : "var(--fc-color-text)",
              }}
            >
              <kbd
                className="shrink-0 rounded px-1 font-data text-2xs"
                style={{
                  background:
                    i === activo
                      ? "color-mix(in srgb, var(--fc-color-on-action) 22%, transparent)"
                      : "var(--fc-color-surface)",
                  color: i === activo ? "var(--fc-color-on-action)" : "var(--fc-color-text-muted)",
                }}
              >
                {s.codigo}
              </kbd>
              <span className="truncate">{s.nombre}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * El menú de usuario: botón + menú propio alineado a la derecha del disparador
 * (el DropdownMenu del sistema de diseño lo centra bajo el botón y no expone
 * alineación, así que se ve descentrado). Cierra al hacer clic fuera o Escape.
 */
function MenuUsuario({ nombre, onSalir }: { nombre: string; onSalir: () => void }) {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!abierto) return;
    const cerrar = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAbierto(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setAbierto(false);
    };
    document.addEventListener("mousedown", cerrar);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", cerrar);
      document.removeEventListener("keydown", esc);
    };
  }, [abierto]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={abierto}
        onClick={() => setAbierto((v) => !v)}
        className="flex items-center gap-2 rounded-control border border-border-control bg-surface-raised py-1.5 pl-1.5 pr-2.5 text-sm font-medium text-text hover:border-border"
      >
        <Avatar nombre={nombre} size={26} />
        <span className="max-w-[10rem] truncate">{nombre}</span>
        <ChevronDown size={16} className="text-text-muted" />
      </button>
      {abierto ? (
        <div className="absolute right-0 top-full z-50 mt-1 min-w-[11rem] rounded-panel border border-border bg-surface p-1 shadow-sm">
          <button
            type="button"
            onClick={() => {
              setAbierto(false);
              onSalir();
            }}
            className="w-full rounded-control px-3 py-2 text-left text-sm text-text hover:bg-surface-raised"
          >
            Cerrar sesión
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Ayuda de atajos (F1): lista las teclas F y a dónde saltan, estilo Dominium.
 * Se cierra con Escape, clic fuera, o la ✕. El manejo de teclado global vive en
 * AppShell; aquí solo se pinta.
 */
function AyudaAtajos({ onCerrar }: { onCerrar: () => void }) {
  return (
    <div
      className="fixed inset-0 z-[70] grid place-items-center p-4"
      style={{ background: "color-mix(in srgb, var(--fc-color-text) 45%, transparent)" }}
      onClick={onCerrar}
      role="dialog"
      aria-modal="true"
      aria-label="Atajos de teclado"
    >
      <div
        className="w-full max-w-md rounded-panel border border-border bg-surface p-5 shadow-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-brand text-lg font-bold">Atajos de teclado</h2>
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="rounded-control px-1.5 text-text-muted hover:text-text"
          >
            ×
          </button>
        </div>
        <p className="mb-1.5 font-data text-2xs font-semibold uppercase tracking-[0.16em] text-text-muted">
          Ahora mismo
        </p>
        <ul className="flex flex-col gap-1">
          {[
            { nombre: "Guardar (en formularios)", tecla: "Alt+G" },
            { nombre: "Nuevo / empezar de cero", tecla: "Alt+N" },
            { nombre: "Abrir / cerrar esta ayuda", tecla: "F1" },
          ].map((a) => (
            <li
              key={a.tecla}
              className="flex items-center justify-between gap-4 rounded-control px-2 py-1.5 hover:bg-surface-raised"
            >
              <span className="text-sm">{a.nombre}</span>
              <kbd className="rounded border border-border-control/60 px-1.5 py-0.5 font-data text-2xs font-semibold text-text-muted">
                {a.tecla}
              </kbd>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-text-muted">
          Más memoria muscular de Dominium llegará con las pantallas de captura: <b>F2</b> buscar por
          nombre, <b>F3</b> por código de barras, <b>F4</b> por código interno, <b>Enter</b> = siguiente
          celda. Se dejan libres para eso, ya no saltan de pantalla.
        </p>
      </div>
    </div>
  );
}
